import { AnyAction } from 'redux';
import { Adressvorschlag, App } from '@ps20-frontend/frontend-models';
import {
  AdressvorschlagFailedPayload,
  AdressvorschlagPayload,
  BeginnAblaufGeaendert,
  CALLER_ID,
  GlobalActions,
  ProduktDeleted,
} from '@ps20-frontend/shop-integration-redux';
import { IAppState } from '@ps20-frontend/apps/checkout-store';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE } from '@ps20-frontend/utils';
import { CheckoutActions } from '../checkout.actions';
import { JagdscheinChanged } from '../action-types/jagdscheinChanged';
import { selectJhpProdukt } from '../selector/produkt.selector';

export interface JhpAngaben {
  jagdschein: Jagdschein;
  jagdscheinAblaufdatum: string;
  jagdBehoerde: Jagdbehoerde;
}

export interface Jagdbehoerde {
  behoerdename: string;
  plz: string;
  ort: string;
  strasse: string;
  hausnummer: string;
  adressvorschlag: Adressvorschlag;
}

export enum Jagdschein {
  JAHRES_JAGDSCHEIN = 'jahres_jagdschein',
  DREI_JAHRES_JAGDSCHEIN = 'drei_jahres_jagdschein',
}

export const jhpAngabenReducer = function (
  sectionState: JhpAngaben = null,
  action: AnyAction,
  state: IAppState,
): JhpAngaben {
  switch (action.type) {
    case CheckoutActions.JAGDSCHEIN_CHANGED: {
      const actionPayload: JagdscheinChanged = action.payload;
      return {
        ...sectionState,
        jagdschein: actionPayload.jagdschein,
        jagdscheinAblaufdatum: calculateAblaufFromJagdschein(
          selectJhpProdukt(state).ablauf,
          actionPayload.jagdschein,
          selectJhpProdukt(state).beginn,
        ),
      };
    }
    case GlobalActions.UPDATE_BEGINN_ABLAUF_OF_PRODUKTE: {
      const actionPayload: BeginnAblaufGeaendert[] = action.payload;
      const matchingUpdate = actionPayload.find(({ appId }) => appId === App.JHP);
      if (matchingUpdate) {
        return {
          ...sectionState,
          jagdscheinAblaufdatum: calculateAblaufFromJagdschein(
            matchingUpdate.ablauf,
            sectionState.jagdschein,
            matchingUpdate.beginn,
          ),
        };
      }
      return sectionState;
    }
    case CheckoutActions.JAGDBEHOERDE_NAME_CHANGED: {
      return {
        ...sectionState,
        jagdBehoerde: { ...sectionState.jagdBehoerde, behoerdename: action.payload },
      };
    }
    case CheckoutActions.JAGDBEHOERDE_STRASSE_CHANGED: {
      return {
        ...sectionState,
        jagdBehoerde: { ...sectionState.jagdBehoerde, strasse: action.payload },
      };
    }
    case CheckoutActions.JAGDBEHOERDE_HAUSNUMMER_CHANGED: {
      return {
        ...sectionState,
        jagdBehoerde: { ...sectionState.jagdBehoerde, hausnummer: action.payload },
      };
    }
    case CheckoutActions.JAGDBEHOERDE_ORT_CHANGED: {
      return {
        ...sectionState,
        jagdBehoerde: { ...sectionState.jagdBehoerde, ort: action.payload },
      };
    }
    case CheckoutActions.JAGDBEHOERDE_PLZ_CHANGED: {
      return {
        ...sectionState,
        jagdBehoerde: {
          ...sectionState.jagdBehoerde,
          plz: action.payload,
          ort: null,
          strasse: null,
          hausnummer: null,
          adressvorschlag: {
            lastError: null,
            orte: [],
            strassen: [],
          },
        },
      };
    }
    case GlobalActions.ADDRESSVORSCHLAG_SUCCESSFUL: {
      const payload: AdressvorschlagPayload = action.payload;
      if (payload.callerId !== CALLER_ID.JHP_JAGDBEHOERDE) {
        return sectionState;
      }
      return {
        ...sectionState,
        jagdBehoerde: {
          ...sectionState.jagdBehoerde,
          adressvorschlag: {
            ...sectionState.jagdBehoerde.adressvorschlag,
            lastError: null,
            orte: payload.orte,
            strassen: payload.strassen,
          },
          ort: payload.orte?.length === 1 ? payload.orte[0] : null,
        },
      };
    }
    case GlobalActions.ADDRESSVORSCHLAG_FAILED: {
      const payload: AdressvorschlagFailedPayload = action.payload;
      if (payload.callerId !== CALLER_ID.JHP_JAGDBEHOERDE) {
        return sectionState;
      }
      return {
        ...sectionState,
        jagdBehoerde: {
          ...sectionState.jagdBehoerde,
          adressvorschlag: {
            lastError: payload.reason,
            orte: [],
            strassen: [],
          },
        },
      };
    }
    case GlobalActions.PRODUKT_DELETED: {
      const produktDeletedPayload: ProduktDeleted = action.payload;
      if (produktDeletedPayload.angebotsIds.some(angebotsId => angebotsId.appId === App.JHP)) {
        return {
          ...sectionState,
          jagdschein: null,
          jagdscheinAblaufdatum: '',
          jagdBehoerde: {
            behoerdename: '',
            ort: null,
            strasse: null,
            hausnummer: null,
            plz: null,
            adressvorschlag: {
              lastError: null,
              strassen: [],
              orte: [],
            },
          },
        };
      }
      return sectionState;
    }
    default:
      return sectionState;
  }
};

function calculateAblaufFromJagdschein(
  ablauf: string,
  jagdschein: Jagdschein,
  beginn: string,
): string {
  const year_offset = dayjs(beginn, DATEFORMAT_LOCAL_STATE).month() < 3 ? 0 : 1;
  const yearBeginn = dayjs(beginn, DATEFORMAT_LOCAL_STATE).year();
  return jagdschein === Jagdschein.DREI_JAHRES_JAGDSCHEIN
    ? ablauf
    : dayjs()
        .date(1)
        .month(3)
        .year(yearBeginn)
        .add(year_offset, 'year')
        .format(DATEFORMAT_LOCAL_STATE);
}
